//@ts-nocheck
import * as PropTypes from 'prop-types';
import { Box, InputLabel, FormHelperText, Typography } from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { convertInputDate } from '../../utils/dateUtility';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { XGInput, XGDatePicker, XGButton } from 'xg-cl';
import XGTextarea from '../../components/ProseTextarea/ProseTextarea';
import pdf from '../../assets/images/pdf.png';
import PassengersCheckBoxForm from '../../components/Booking/BookingPassengers/PassengersCheckBoxForm/PassengersCheckBoxForm';
import PassengersRadioForm from '../../components/Booking/BookingPassengers/PassengersRadioForm/PassengersRadioForm';
import LineItemAttachmentManager from '../../components/LineItem/LineItemAttachmentManager/LineItemAttachmentManager';
import {
  DATE,
  TEXTAREA,
  CHECKBOX,
  RADIOBUTTON,
  BOOLEAN,
  PHONE,
  NUMBER,
  EMAIL,
  TEXTBOX
} from '../../constants/passengers';
import {
  maxPassengerFieldUploadFiles,
  maxPassengerFieldUploadSize,
  supportedAttachmentAccept,
  supportedAttachmentExtensions,
  supportedAttachmentMimeTypes
} from '../../constants/files';
import { bytesToMiB } from '../../utils/fileHelper';
import {
  getExistingPassengerFieldFiles,
  isFileUploadPassengerFieldType,
  isSignableDocumentPassengerFieldType
} from '../../utils/passengerFieldTypes';

const SecondRoundField = ({
  id,
  formErrors,
  setFormErrors,
  handleFieldChange,
  handleFieldFileChange,
  handleFieldFileError,
  getFieldValue,
  getFieldFiles,
  field,
  isSubmitting,
  onSignableDocumentSign
}) => {
  const { t } = useTranslation(['trip', 'common', 'errors', 'passengers']);
  const { enablePassengerFieldUploads, enablePaxWaivers } = useFlags();

  const getDate = (value) => {
    const newValue = Array.isArray(value) ? value[0] : value;
    if (!newValue) return null;
    const date = new Date(newValue);
    const offsetInMs = date.getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() + offsetInMs);
  };

  const getSignedDateLabel = (field = {}) => {
    const signedDocument = field?.signableDocumentState?.signedDocument || {};
    const signedAt =
      signedDocument?.signedDateTime ||
      signedDocument?.signeddatetime ||
      signedDocument?.signedAt ||
      signedDocument?.signedat ||
      null;

    if (!signedAt) {
      return '';
    }

    const signedDate = new Date(signedAt);

    if (Number.isNaN(signedDate.getTime())) {
      return '';
    }

    return format(signedDate, t('common:dateFormat'));
  };

  //  Render field component based on type
  const renderField = (field, disabled = false) => {
    const fieldType = field.fieldtype?.key;
    const fieldValue = getFieldValue(field);
    const isRequired = field.assignmenttype === 'required';
    const fieldDescription = field?.description
      ? ` - ${field?.description.trim()}`
      : '';

    // Get error for this field
    const fieldError = formErrors[field.trippassengerfieldid] || field.error;
    const hasError = Boolean(fieldError);
    const isSignableDocumentField =
      enablePaxWaivers && isSignableDocumentPassengerFieldType(field.fieldtype);

    const label = (
      <>
        {field?.title}
        {isRequired && (
          <span className='passenger-fields-form__req-field'> *</span>
        )}
        {fieldDescription}
      </>
    );

    const commonProps = {
      key: field.trippassengerfieldid,
      label,
      name: field.title.toLowerCase().replace(/\s+/g, ''),
      id: `passenger-field-${field.trippassengerfieldid}-${id}`,
      value: fieldValue,
      disabled,
      error: hasError,
      helpertext: fieldError,
      onChange: (e) => {
        if (!disabled) {
          if (formErrors[field.trippassengerfieldid]) {
            setFormErrors((prev) => ({
              ...prev,
              [field.trippassengerfieldid]: null
            }));
          }
          handleFieldChange(field.trippassengerfieldid, e.target.value);
        }
      }
    };

    if (isSignableDocumentField) {
      const signableDocumentState = field?.signableDocumentState ?? {};
      const isSigned = Boolean(signableDocumentState.documentReady);
      const signedDate = getSignedDateLabel(field);
      const fieldId = `passenger-signable-document-${field.trippassengerfieldid}-${id}`;

      return (
        <Box
          className={`passenger-waiver-field ${isSigned ? 'passenger-waiver-field--signed' : ''}`}
        >
          <Box className='passenger-waiver-field__header'>
            <Box>
              <InputLabel
                className='xg-input-label passenger-waiver-field__label'
                htmlFor={fieldId}
              >
                {field?.title}
                {isRequired && (
                  <span className='passenger-fields-form__req-field'> *</span>
                )}
              </InputLabel>
              {field?.description && (
                <Typography className='passenger-waiver-field__description'>
                  {field.description}
                </Typography>
              )}
            </Box>
          </Box>

          {!isSigned && (
            <XGButton
              className='passenger-waiver-field__action-btn'
              id={fieldId}
              type='secondary'
              variant='outlined'
              size='medium'
              startIcon={<img src={pdf} alt='PDF' />}
              text={
                field?.isSigning
                  ? t('passengers:documentInProgress')
                  : t('passengers:clickToSignPdf')
              }
              disabled={disabled || field?.isSigning}
              onClick={() => {
                if (!disabled && !field?.isSigning) {
                  onSignableDocumentSign?.(field);
                }
              }}
            />
          )}

          {isSigned && (
            <Box className='passenger-waiver-field__signed-row'>
              <span className='passenger-waiver-field__signed-icon'>✓</span>
              <span className='passenger-waiver-field__signed-label'>
                {t('common:signed')}
              </span>
              {signedDate && (
                <span className='passenger-waiver-field__signed-date'>
                  {signedDate}
                </span>
              )}
            </Box>
          )}

          {fieldError && (
            <FormHelperText className='input-form-helper-text--error'>
              {fieldError}
            </FormHelperText>
          )}
        </Box>
      );
    }

    if (
      enablePassengerFieldUploads &&
      isFileUploadPassengerFieldType(field.fieldtype)
    ) {
      const existingFiles = getExistingPassengerFieldFiles(field) ?? [];
      const selectedFiles = getFieldFiles(field);

      return (
        <Box>
          <InputLabel
            className='xg-input-label'
            htmlFor={`passenger-${field?.key}-input-${id}`}
          >
            {label}
          </InputLabel>
          <LineItemAttachmentManager
            attachments={selectedFiles}
            existingAttachments={existingFiles}
            onChange={(files) => {
              if (formErrors[field.trippassengerfieldid]) {
                setFormErrors((prev) => ({
                  ...prev,
                  [field.trippassengerfieldid]: null
                }));
              }
              handleFieldFileChange(field.trippassengerfieldid, files);
            }}
            onError={(error) => {
              handleFieldFileError(field.trippassengerfieldid, error);
            }}
            maxAttachments={maxPassengerFieldUploadFiles}
            maxSize={maxPassengerFieldUploadSize}
            label={t('common:addFile', 'Add File')}
            uploadLabel={t('common:uploadAttachment', 'Upload Attachment')}
            accept={supportedAttachmentAccept}
            acceptedMimeTypes={supportedAttachmentMimeTypes}
            acceptedExtensions={supportedAttachmentExtensions}
            error={fieldError}
            hint={t('common:passengerFieldUploadHint', {
              maxFileSize: bytesToMiB(maxPassengerFieldUploadSize),
              maxFiles: maxPassengerFieldUploadFiles
            })}
            existingAttachmentsTitle={t(
              'common:previouslyUploaded',
              'Previously Uploaded'
            )}
            addMoreFilesTitle={t('common:addMoreFiles', 'Add More Files')}
            existingAttachmentBadge={t('common:fromCheckout', 'From Checkout')}
            showExistingAttachmentDates
            showInlineHint
            disabled={disabled}
          />
        </Box>
      );
    }

    switch (fieldType) {
      case TEXTBOX:
        return (
          <Box>
            <XGInput
              {...commonProps}
              type='text'
              maxLength={field.trippassengerfieldconfig?.maxcharcount}
            />
            <>
              {hasError && (
                <div className='input-helper-text-container'>
                  <FormHelperText className='input-form-helper-text--error'>
                    {fieldError}
                  </FormHelperText>
                </div>
              )}
            </>
          </Box>
        );

      case NUMBER:
        return (
          <Box>
            <XGInput
              {...commonProps}
              type='number'
              maxLength={field.trippassengerfieldconfig?.maxcharcount}
            />
            <>
              {hasError && (
                <div className='input-helper-text-container'>
                  <FormHelperText className='input-form-helper-text--error'>
                    {fieldError}
                  </FormHelperText>
                </div>
              )}
            </>
          </Box>
        );

      case EMAIL:
        return (
          <Box>
            <XGInput {...commonProps} type='email' />
            <>
              {hasError && (
                <div className='input-helper-text-container'>
                  <FormHelperText className='input-form-helper-text--error'>
                    {fieldError}
                  </FormHelperText>
                </div>
              )}
            </>
          </Box>
        );

      case RADIOBUTTON:
      case BOOLEAN:
        return (
          <Box>
            <PassengersRadioForm
              id={`passenger-${field?.key}-input-${id}`}
              formId={id}
              field={{
                ...field,
                fieldId: field?.trippassengerfieldid,
                value: fieldValue,
                error: fieldError
              }}
              handleChange={(value) => {
                if (formErrors[field.trippassengerfieldid]) {
                  setFormErrors((prev) => ({
                    ...prev,
                    [field.trippassengerfieldid]: null
                  }));
                }
                handleFieldChange(field?.trippassengerfieldid, value);
              }}
              customTitle={label}
              disabled={disabled}
            />
            {fieldError && (
              <FormHelperText className='input-form-helper-text--error'>
                {fieldError}
              </FormHelperText>
            )}
          </Box>
        );

      case PHONE:
        return (
          <Box
            className='w-100 passenger-form__input-container'
            key={field?.trippassengerfieldid}
          >
            <InputLabel
              className='xg-input-label phone-input-label'
              htmlFor={`passenger-${field?.key}-input-${id}`}
            >
              {label}
            </InputLabel>
            <MuiTelInput
              className={`phone-input ${field?.error ? ' passenger-form__phone-input-error ' : ''}`}
              value={fieldValue}
              name={field?.key}
              id={`passenger-${field?.key}-input-${id}`}
              onChange={(value) => {
                if (!disabled) {
                  if (formErrors[field.trippassengerfieldid]) {
                    setFormErrors((prev) => ({
                      ...prev,
                      [field.trippassengerfieldid]: null
                    }));
                  }
                  handleFieldChange(field.trippassengerfieldid, value);
                }
              }}
              defaultCountry='US'
              inputProps={{
                autoComplete: 'off',
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }}
              disabled={disabled}
            />
            {fieldError && (
              <FormHelperText className='input-form-helper-text--error'>
                {fieldError}
              </FormHelperText>
            )}
          </Box>
        );

      case TEXTAREA:
        return (
          <Box
            className='w-100 passenger-form__input-container'
            key={field?.trippassengerfieldid}
          >
            <XGTextarea
              placeholder={`${t('common:enter')} ${field?.title?.toLowerCase()}`}
              label={label}
              id={`passenger-${field?.key}-input-${id}`}
              type='text'
              name={field?.key}
              value={fieldValue}
              onChange={(evt) => {
                if (!disabled) {
                  if (formErrors[field.trippassengerfieldid]) {
                    setFormErrors((prev) => ({
                      ...prev,
                      [field.trippassengerfieldid]: null
                    }));
                  }
                  handleFieldChange(
                    field.trippassengerfieldid,
                    evt.target.value
                  );
                }
              }}
              inputProps={{
                maxLength: 1000,
                autoComplete: 'off'
              }}
              error={hasError}
              helpertext={fieldError}
              minRows={3}
              disabled={disabled}
            />

            <>
              {hasError && (
                <div className='input-helper-text-container '>
                  <FormHelperText className='input-form-helper-text--error'>
                    {fieldError}
                  </FormHelperText>{' '}
                </div>
              )}
            </>

            <span className='input-form-length-hint input-form-length-hint--compact'>
              {fieldValue?.length || 0}/1000
            </span>
          </Box>
        );

      case CHECKBOX:
        return (
          <Box>
            <PassengersCheckBoxForm
              key={field.trippassengerfieldid}
              id={`passenger-${field?.key}-input-${id}`}
              field={{ ...field, fieldId: field.trippassengerfieldid }}
              selectedOptions={fieldValue}
              formId={field?.trippassengerfieldid}
              handleOnChange={(formId, fieldId, newOptions) => {
                if (formErrors[fieldId]) {
                  setFormErrors((prev) => ({
                    ...prev,
                    [fieldId]: null
                  }));
                }
                handleFieldChange(fieldId, newOptions);
              }}
              customTitle={label}
              disabled={disabled}
            />
            {fieldError && (
              <FormHelperText className='input-form-helper-text--error'>
                {fieldError}
              </FormHelperText>
            )}
          </Box>
        );

      case DATE:
        return (
          <Box
            className='w-100 passenger-form__input-container'
            key={field.trippassengerfieldid}
          >
            <XGDatePicker
              className={`${field?.error ? ' passenger-form__input-error ' : ''}`}
              style={{ width: '100%' }}
              format={`${t('common:dateFormat')}`}
              id={`passenger-${field?.key}-input-${id}`}
              name={field?.key}
              label={label}
              autoComplete='off'
              value={fieldValue ? getDate(fieldValue) : null}
              onChange={(selectedDate) => {
                if (selectedDate.getFullYear() > 1900) {
                  if (!disabled) {
                    if (formErrors[field.trippassengerfieldid]) {
                      setFormErrors((prev) => ({
                        ...prev,
                        [field.trippassengerfieldid]: null
                      }));
                    }
                    handleFieldChange(
                      field.trippassengerfieldid,
                      convertInputDate(selectedDate).toISOString()
                    );
                  }
                }
              }}
              error={hasError}
              hint={fieldError}
              disabled={disabled}
            />
          </Box>
        );

      default:
        return (
          <Box>
            <XGInput
              {...commonProps}
              type='text'
              maxLength={field.trippassengerfieldconfig?.maxcharcount}
            />
            <>
              {hasError && (
                <div className='input-helper-text-container'>
                  <FormHelperText className='input-form-helper-text--error'>
                    {fieldError}
                  </FormHelperText>
                </div>
              )}
            </>
          </Box>
        );
    }
  };

  return (
    <Box className='w-100 passenger-form__input-container'>
      {renderField(field, isSubmitting)}
    </Box>
  );
};

SecondRoundField.propTypes = {
  id: PropTypes.string.isRequired,
  formErrors: PropTypes.object,
  setFormErrors: PropTypes.func,
  handleFieldChange: PropTypes.func,
  handleFieldFileChange: PropTypes.func,
  handleFieldFileError: PropTypes.func,
  getFieldValue: PropTypes.func,
  getFieldFiles: PropTypes.func,
  field: PropTypes.object,
  isSubmitting: PropTypes.bool,
  onSignableDocumentSign: PropTypes.func
};

export default SecondRoundField;
